// Module PUR (aucun import) : contrat marqueurs [?...] + conversions markdown ⇄ TipTap ⇄ blocs PDF.
// ⚠️ interpolateTemplate est DUPLIQUÉE dans supabase/functions/generate-document/index.ts
// (l'EF doit être autonome, le bundler MCP ne résout pas ../_shared/) — garder identiques.

export const MARKER_RE = /\[\?[^\]\n]{1,160}\]/g;

export function extractMarkers(text: string): string[] {
  return text.match(MARKER_RE) ?? [];
}

export function interpolateTemplate(
  bodyMd: string,
  ctx: Record<string, string | null | undefined>,
): { text: string; missing: string[] } {
  const missing: string[] = [];
  const text = bodyMd.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (_m, key: string) => {
    const val = ctx[key];
    if (val === undefined || val === null || val === '') {
      missing.push(key);
      return `[?${key}]`;
    }
    return val;
  });
  return { text, missing };
}

export interface TiptapMark { type: 'bold' | 'italic' | 'highlight' }
export interface TiptapNode {
  type: string;
  attrs?: Record<string, unknown>;
  content?: TiptapNode[];
  text?: string;
  marks?: TiptapMark[];
}

function textNode(text: string, marks: TiptapMark[]): TiptapNode {
  return marks.length ? { type: 'text', text, marks } : { type: 'text', text };
}

/** Découpe une ligne en nœuds texte : **gras**, *italique*, marqueurs [?...] (pas d'imbrication). */
function parseInline(text: string): TiptapNode[] {
  const nodes: TiptapNode[] = [];
  const re = /(\*\*([^*\n]+)\*\*)|(\*([^*\n]+)\*)|(\[\?[^\]\n]{1,160}\])/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text))) {
    if (m.index > last) nodes.push(textNode(text.slice(last, m.index), []));
    if (m[2] !== undefined) nodes.push(textNode(m[2], [{ type: 'bold' }]));
    else if (m[4] !== undefined) nodes.push(textNode(m[4], [{ type: 'italic' }]));
    else if (m[5] !== undefined) nodes.push(textNode(m[5], [{ type: 'highlight' }]));
    last = re.lastIndex;
  }
  if (last < text.length) nodes.push(textNode(text.slice(last), []));
  return nodes;
}

const HEADING_RE = /^(#{1,3})\s+(.*)$/;
const BULLET_RE = /^[-*]\s+/;
const ORDERED_RE = /^\d+[.)]\s+/;

function listItem(text: string): TiptapNode {
  return { type: 'listItem', content: [{ type: 'paragraph', content: parseInline(text) }] };
}

export function markdownToTiptap(md: string): TiptapNode {
  const content: TiptapNode[] = [];
  const lines = md.replace(/\r\n/g, '\n').split('\n');
  let i = 0;
  while (i < lines.length) {
    const line = lines[i]!;
    if (line.trim() === '') { i++; continue; }
    const h = line.match(HEADING_RE);
    if (h) {
      content.push({ type: 'heading', attrs: { level: h[1]!.length }, content: parseInline(h[2]!) });
      i++; continue;
    }
    if (BULLET_RE.test(line)) {
      const items: TiptapNode[] = [];
      while (i < lines.length && BULLET_RE.test(lines[i]!)) { items.push(listItem(lines[i]!.replace(BULLET_RE, ''))); i++; }
      content.push({ type: 'bulletList', content: items });
      continue;
    }
    if (ORDERED_RE.test(line)) {
      const items: TiptapNode[] = [];
      while (i < lines.length && ORDERED_RE.test(lines[i]!)) { items.push(listItem(lines[i]!.replace(ORDERED_RE, ''))); i++; }
      content.push({ type: 'orderedList', content: items });
      continue;
    }
    // Paragraphe : lignes consécutives non vides, jointes par un espace.
    const buf = [line];
    i++;
    while (
      i < lines.length && lines[i]!.trim() !== ''
      && !HEADING_RE.test(lines[i]!) && !BULLET_RE.test(lines[i]!) && !ORDERED_RE.test(lines[i]!)
    ) { buf.push(lines[i]!); i++; }
    content.push({ type: 'paragraph', content: parseInline(buf.join(' ')) });
  }
  return { type: 'doc', content: content.length ? content : [{ type: 'paragraph', content: [] }] };
}

/** Texte brut d'un document TipTap — blocs de premier niveau séparés par \n. */
export function tiptapText(node: TiptapNode): string {
  if (node.type === 'text') return node.text ?? '';
  const inner = (node.content ?? []).map(tiptapText);
  if (node.type === 'doc') return inner.filter((s) => s !== '').join('\n');
  return inner.join('');
}

export interface PdfSegment { text: string; bold?: boolean; italic?: boolean; marker?: boolean }
export interface PdfBlock {
  kind: 'heading1' | 'heading2' | 'heading3' | 'paragraph' | 'bullet' | 'ordered';
  segments: PdfSegment[];
}

function nodeSegments(nodes: TiptapNode[] | undefined): PdfSegment[] {
  const out: PdfSegment[] = [];
  for (const n of nodes ?? []) {
    if (n.type === 'text') {
      const marks = (n.marks ?? []).map((m) => m.type);
      out.push({
        text: n.text ?? '',
        ...(marks.includes('bold') ? { bold: true } : {}),
        ...(marks.includes('italic') ? { italic: true } : {}),
        ...(marks.includes('highlight') ? { marker: true } : {}),
      });
    } else {
      out.push(...nodeSegments(n.content));
    }
  }
  return out;
}

export function tiptapToPdfBlocks(doc: TiptapNode): PdfBlock[] {
  const blocks: PdfBlock[] = [];
  for (const node of doc.content ?? []) {
    if (node.type === 'heading') {
      const level = Number(node.attrs?.level ?? 1);
      const kind = level === 1 ? 'heading1' : level === 2 ? 'heading2' : 'heading3';
      blocks.push({ kind, segments: nodeSegments(node.content) });
    } else if (node.type === 'paragraph') {
      blocks.push({ kind: 'paragraph', segments: nodeSegments(node.content) });
    } else if (node.type === 'bulletList' || node.type === 'orderedList') {
      const kind = node.type === 'bulletList' ? 'bullet' : 'ordered';
      for (const item of node.content ?? []) blocks.push({ kind, segments: nodeSegments(item.content) });
    }
  }
  return blocks;
}
